import { ancestorChain } from './tree.js';
export const UNASSIGNED = 'Unassigned';
/** Squad roles in the order a squad box should read: who leads, then the shape of the team. */
const ROLE_ORDER = {
    lead: 0,
    product: 1,
    design: 2,
    engineer: 3,
    member: 4,
};
export function compareRoles(a, b) {
    return ROLE_ORDER[a] - ROLE_ORDER[b];
}
// ---------------------------------------------------------------------------
// Squad lookups
// ---------------------------------------------------------------------------
export function emptySquadDirectory() {
    return { updatedAt: new Date().toISOString(), squads: [] };
}
export function emptySquadOverrides() {
    return { updatedAt: new Date().toISOString(), newSquads: [], squadEdits: {}, disbandedSquads: [] };
}
/**
 * The teams exactly as the HRIS reports them: one flat squad per distinct value
 * of the TEAMS column, holding everyone who carries it.
 *
 * This is the floor of the three-layer fold and it is deliberately dumb. There
 * is no hierarchy, no lead and no colour here because Snowflake does not know
 * any of that — it is the overrides layer's job to add it, and everyone nobody
 * has curated still shows up correctly with no work at all.
 *
 * Ordering is A–Z rather than insertion order so that two pulls of the same org
 * produce byte-identical output, which is what keeps a re-publish from looking
 * like a change.
 */
export function derivedSquads(people) {
    const byId = new Map();
    for (const person of people) {
        for (const team of person.teams) {
            const name = team.trim();
            if (!name)
                continue;
            const id = squadId(name);
            const squad = byId.get(id) ?? {
                id,
                name,
                description: '',
                members: [],
                parentId: null,
                color: null,
                fromHris: true,
            };
            // Two spellings that slug the same ("Artemis" and "artemis ") are one
            // team; the first spelling seen wins so the name is at least a real one.
            if (!squad.members.some((member) => member.personId === person.id)) {
                squad.members.push({ personId: person.id, role: 'member' });
            }
            byId.set(id, squad);
        }
    }
    const squads = [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
    for (const squad of squads)
        squad.members.sort((a, b) => a.personId.localeCompare(b.personId));
    return { updatedAt: '', squads };
}
/**
 * How the teams really are: what Snowflake says, with our curation on top.
 *
 * Every view that is not showing a plan reads this, and it is also the base a
 * scenario's team layer is measured against — so a change entry means "different
 * from how the teams actually are", not "different from a raw HRIS column".
 */
export function baseSquadDirectory(people, overrides) {
    const derived = derivedSquads(people);
    const composed = effectiveSquads(derived, overrides);
    return { ...composed, updatedAt: overrides?.updatedAt ?? derived.updatedAt };
}
/** `Artemis` -> `sqd-artemis`. Stable, so a squad keeps its id when renamed elsewhere. */
export function squadId(name) {
    const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
        .slice(0, 48);
    return `sqd-${slug || 'squad'}`;
}
/**
 * The squad a scenario's overrides are measured against: the directory entry,
 * or the definition of a team the scenario itself invented.
 */
export function baseSquad(directory, layer, squadId) {
    return (directory.squads.find((squad) => squad.id === squadId) ??
        layer?.newSquads.find((squad) => squad.id === squadId));
}
/**
 * Fold one layer onto the teams beneath it: what is there, plus the teams the
 * layer invents, minus the ones it stands down, with its overrides applied.
 *
 * Used twice, and the fact that it is the same function both times is the
 * point. Over the flat list from Snowflake it produces the teams as they really
 * are; over that, it produces the teams as a plan proposes them. One set of
 * rules about what an override means, so a rename in the base and a rename in a
 * scenario cannot drift apart.
 *
 * Everything that renders or groups reads the result rather than a stored
 * directory, so the team view shows the plan for the same reason the chart does
 * — with no layer, or a layer that proposes nothing, it is unchanged.
 */
export function effectiveSquads(directory, layer) {
    if (!layer)
        return directory;
    const disbanded = new Set(layer.disbandedSquads);
    const squads = [];
    for (const squad of [...directory.squads, ...layer.newSquads]) {
        if (disbanded.has(squad.id))
            continue;
        const edit = layer.squadEdits[squad.id];
        if (!edit) {
            squads.push(squad);
            continue;
        }
        const members = new Map(squad.members.map((member) => [member.personId, member.role]));
        for (const [personId, role] of Object.entries(edit.members ?? {})) {
            if (role === null)
                members.delete(personId);
            else
                members.set(personId, role);
        }
        squads.push({
            ...squad,
            name: edit.name ?? squad.name,
            description: edit.description ?? squad.description,
            parentId: edit.parentId === undefined ? squad.parentId : edit.parentId,
            color: edit.color === undefined ? squad.color : edit.color,
            members: [...members].map(([personId, role]) => ({ personId, role })),
        });
    }
    return { updatedAt: directory.updatedAt, squads };
}
/** Whether following a squad's parents comes back round to itself. */
function loops(directory, squadId) {
    const byId = new Map(directory.squads.map((squad) => [squad.id, squad]));
    const seen = new Set();
    let current = byId.get(squadId)?.parentId ?? null;
    while (current) {
        if (current === squadId)
            return true;
        if (seen.has(current))
            return false;
        seen.add(current);
        current = byId.get(current)?.parentId ?? null;
    }
    return false;
}
/**
 * A squad and its ancestors, outermost first: Athena inside Data reads
 * [Data, Athena]. A parent that has gone missing, or a loop, stops the walk —
 * the chart still has to draw.
 */
export function squadChain(directory, squadId) {
    const byId = new Map(directory.squads.map((squad) => [squad.id, squad]));
    const chain = [];
    const seen = new Set();
    let current = byId.get(squadId);
    while (current && !seen.has(current.id)) {
        seen.add(current.id);
        chain.unshift(current);
        current = current.parentId ? byId.get(current.parentId) : undefined;
    }
    return chain;
}
/** personId -> the squads they are on, with the hat they wear on each. */
export function squadsByPerson(directory) {
    const byPerson = new Map();
    for (const squad of directory.squads) {
        for (const member of squad.members) {
            const list = byPerson.get(member.personId) ?? [];
            list.push({ squad, role: member.role });
            byPerson.set(member.personId, list);
        }
    }
    return byPerson;
}
/**
 * Drop memberships for people who are no longer in the org, and de-duplicate
 * anyone listed twice on one squad. A Snowflake refresh can retire people, and
 * a stale membership would otherwise render an empty slot forever.
 */
export function reconcileSquads(model, directory) {
    const warnings = [];
    const known = new Set(directory.squads.map((squad) => squad.id));
    const squads = directory.squads.map((squad) => {
        const seen = new Set();
        const members = [];
        for (const member of squad.members) {
            if (seen.has(member.personId))
                continue;
            seen.add(member.personId);
            if (!model.byId.has(member.personId)) {
                warnings.push(`Removed someone from “${squad.name}” who is no longer in the org.`);
                continue;
            }
            members.push(member);
        }
        let parentId = squad.parentId ?? null;
        if (parentId && !known.has(parentId)) {
            warnings.push(`“${squad.name}” was inside a team that no longer exists, so it is top level now.`);
            parentId = null;
        }
        if (parentId && loops(directory, squad.id)) {
            warnings.push(`“${squad.name}” was inside itself, so it is top level now.`);
            parentId = null;
        }
        return { ...squad, members, parentId };
    });
    return { directory: { ...directory, squads }, warnings };
}
/**
 * The value(s) a person contributes at one level.
 *
 * Multi-valued on purpose: someone on two squads belongs under both, which is
 * the honest picture — a shared tech lead really is in two places.
 *
 * A value can be a *chain* rather than a single label, because squads nest:
 * somebody on Athena, which sits inside Data, belongs in a box inside a box.
 * Every other level contributes a chain of one.
 */
function valuesFor(model, person, key, squadsFor, squads) {
    const one = (label) => [
        { labels: [label?.trim() || UNASSIGNED], role: 'member' },
    ];
    switch (key) {
        case 'department':
            return one(person.department);
        case 'location':
            return one(person.location);
        case 'country':
            return one(person.country);
        case 'team':
            return person.teams.length > 0
                ? person.teams.map((team) => ({ labels: [team], role: 'member' }))
                : one(null);
        case 'squad': {
            const memberships = squadsFor.get(person.id) ?? [];
            return memberships.length > 0
                ? memberships.map(({ squad, role }) => ({
                    labels: squadChain(squads, squad.id).map((step) => step.name),
                    role,
                }))
                : one(null);
        }
        case 'org': {
            // The top-level leader they sit under: chain[0] is the root, chain[1] the
            // person heading that part of the company. A root is its own org.
            const chain = ancestorChain(model, person.id);
            const leader = chain.length > 1 ? chain[1] : chain[0];
            return one(leader?.name);
        }
        default: {
            const exhaustive = key;
            return one(String(exhaustive));
        }
    }
}
/**
 * Build a tree out of person attributes instead of the reporting line.
 *
 * The deepest level holds the people; every level above is a container with a
 * headcount. Ordering is largest-first then A–Z at every level, matching how
 * the reporting chart sorts siblings, with "Unassigned" pinned last so it never
 * leads.
 */
export function groupOrg(model, options) {
    const levels = options.levels.slice(0, 3);
    const byId = new Map();
    const rootIds = [];
    const unassigned = new Set();
    if (levels.length === 0) {
        return { byId, rootIds, levels, unassignedCount: 0 };
    }
    const directory = options.squads ?? emptySquadDirectory();
    const squadsFor = squadsByPerson(directory);
    const scope = options.rootId ? new Set(subtreeIds(model, options.rootId)) : null;
    const people = model.people.filter((person) => {
        if (scope && !scope.has(person.id))
            return false;
        if (!options.includeDepartures && model.departures.has(person.id))
            return false;
        return true;
    });
    /*
     * Distinct people per node, not a running total. Someone on two squads walks
     * two paths, and their department must still count them once — only the
     * squad boxes themselves should show them twice.
     */
    const occupants = new Map();
    const ensure = (id, key, label, depth) => {
        const existing = byId.get(id);
        if (existing)
            return existing;
        const node = { id, key, label, depth, childIds: [], members: [], headcount: 0 };
        byId.set(id, node);
        occupants.set(id, new Set());
        return node;
    };
    for (const person of people) {
        // Every combination of values across the levels: two squads and one
        // department puts the person in two places, not one.
        let paths = [[]];
        for (const key of levels) {
            const values = valuesFor(model, person, key, squadsFor, directory);
            paths = paths.flatMap((path) => values.map((value) => {
                if (value.labels[0] === UNASSIGNED)
                    unassigned.add(person.id);
                // A nested squad adds one step per ancestor, so the path can be
                // longer than the list of levels.
                const steps = [];
                for (const label of value.labels) {
                    const parentId = [...path, ...steps].at(-1)?.id ?? '';
                    steps.push({ id: `${parentId}/${key}:${label}`, key, label, role: value.role });
                }
                return [...path, ...steps];
            }));
        }
        for (const path of paths) {
            path.forEach((step, depth) => {
                const node = ensure(step.id, step.key, step.label, depth);
                occupants.get(node.id)?.add(person.id);
                const parentId = depth === 0 ? null : path[depth - 1]?.id;
                if (parentId === null) {
                    if (!rootIds.includes(node.id))
                        rootIds.push(node.id);
                }
                else if (parentId) {
                    const parent = byId.get(parentId);
                    if (parent && !parent.childIds.includes(node.id))
                        parent.childIds.push(node.id);
                }
                if (depth === path.length - 1) {
                    node.members.push({ personId: person.id, role: step.role });
                }
            });
        }
    }
    const order = (ids) => ids.sort((a, b) => {
        const left = byId.get(a);
        const right = byId.get(b);
        if (left.label === UNASSIGNED)
            return 1;
        if (right.label === UNASSIGNED)
            return -1;
        return right.headcount - left.headcount || left.label.localeCompare(right.label);
    });
    for (const node of byId.values())
        node.headcount = occupants.get(node.id)?.size ?? 0;
    order(rootIds);
    const nameOf = (personId) => model.byId.get(personId)?.name ?? personId;
    for (const node of byId.values()) {
        order(node.childIds);
        // Role first so a squad box leads with its lead, then by name — the order
        // people are stored in is an implementation detail and must not show.
        node.members.sort((a, b) => compareRoles(a.role, b.role) || nameOf(a.personId).localeCompare(nameOf(b.personId)));
    }
    return { byId, rootIds, levels, unassignedCount: unassigned.size };
}
function subtreeIds(model, rootId) {
    const ids = [];
    const stack = [rootId];
    const seen = new Set();
    while (stack.length > 0) {
        const id = stack.pop();
        if (seen.has(id))
            continue;
        seen.add(id);
        ids.push(id);
        for (const child of model.childIds.get(id) ?? [])
            stack.push(child);
    }
    return ids;
}
/** One line per group, for the MCP server and for debugging. */
export function renderGroupText(grouped, model) {
    const lines = [];
    const walk = (id, depth) => {
        const node = grouped.byId.get(id);
        if (!node)
            return;
        lines.push(`${'  '.repeat(depth)}${depth > 0 ? '- ' : ''}${node.label} (${node.headcount})`);
        for (const child of node.childIds)
            walk(child, depth + 1);
        for (const member of node.members) {
            const person = model.byId.get(member.personId);
            if (!person)
                continue;
            const role = member.role === 'member' ? '' : ` «${member.role}»`;
            lines.push(`${'  '.repeat(depth + 1)}· ${person.name} — ${person.title}${role} {${person.id}}`);
        }
    };
    for (const root of grouped.rootIds)
        walk(root, 0);
    return lines.join('\n');
}
//# sourceMappingURL=group.js.map